/**
 * Deal Types HTTP client — DT-1 product types / sale types / links / slots.
 */
import { supabase } from "@/lib/supabase";
import { readWorkspaceOrgIdFromStorage } from "@/lib/workspaceOrgStorage";
import type { OpsDealTypeSlotKind } from "@/lib/opsDealTypes/opsDealTypeKeys";

export type FetchResult<T> = { ok: true; status: number; data: T } | { ok: false; status: number; error: string };

export type OpsDealProductType = {
  id: string;
  organization_id: string;
  type_key: string;
  display_name: string;
  description: string;
  sort_order: number;
  is_active: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type OpsDealSaleType = OpsDealProductType;

export type OpsDealTypeSlot = {
  id: string;
  organization_id: string;
  product_type_id: string | null;
  sale_type_id: string | null;
  slot_key: string;
  label: string;
  helper_text: string | null;
  slot_kind: OpsDealTypeSlotKind;
  packet_layer: "ops" | "plant";
  required: boolean;
  sort_order: number;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type OpsDealProductSaleLink = {
  id: string;
  organization_id: string;
  product_type_id: string;
  sale_type_id: string;
  created_at: string;
};

const BASE = "/api/ops/deal-types";

async function dealTypesFetch<T>(path: string, init: RequestInit = {}): Promise<FetchResult<T>> {
  if (!supabase) return { ok: false, status: 503, error: "App is not configured for sign-in." };
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, status: 401, error: "Sign in required." };

  const headers: HeadersInit = {
    Authorization: `Bearer ${token}`,
    ...(init.headers || {}),
  };
  const ws = readWorkspaceOrgIdFromStorage();
  if (ws) (headers as Record<string, string>)["X-Workspace-Organization-Id"] = ws;

  const r = await fetch(path, { ...init, headers });
  const text = await r.text();
  let json: unknown = null;
  try {
    json = JSON.parse(text);
  } catch {
    /* non-JSON */
  }
  if (!r.ok) {
    const err =
      json && typeof json === "object" && json !== null && "error" in json && typeof (json as { error: unknown }).error === "string"
        ? (json as { error: string }).error
        : text || `HTTP ${r.status}`;
    return { ok: false, status: r.status, error: err };
  }
  return { ok: true, status: r.status, data: json as T };
}

function q(resource: string, extra?: Record<string, string>) {
  const p = new URLSearchParams({ resource });
  if (extra) for (const [k, v] of Object.entries(extra)) if (v) p.set(k, v);
  return `${BASE}?${p.toString()}`;
}

export async function listDealProductTypes() {
  return dealTypesFetch<{ ok: true; product_types: OpsDealProductType[] }>(q("product-types"));
}

export async function createDealProductType(body: {
  display_name: string;
  type_key?: string;
  description?: string;
  sort_order?: number;
}) {
  return dealTypesFetch<{ ok: true; product_type: OpsDealProductType }>(q("product-types"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function patchDealProductType(
  id: string,
  body: Partial<{ display_name: string; description: string; sort_order: number; is_active: boolean; type_key: string; metadata: Record<string, unknown> }>,
) {
  return dealTypesFetch<{ ok: true; product_type: OpsDealProductType }>(q("product-types", { id }), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function deleteDealProductType(id: string) {
  return dealTypesFetch<{ ok: true }>(q("product-types", { id }), { method: "DELETE" });
}

export async function listDealSaleTypes() {
  return dealTypesFetch<{ ok: true; sale_types: OpsDealSaleType[] }>(q("sale-types"));
}

export async function createDealSaleType(body: {
  display_name: string;
  type_key?: string;
  description?: string;
  sort_order?: number;
}) {
  return dealTypesFetch<{ ok: true; sale_type: OpsDealSaleType }>(q("sale-types"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function patchDealSaleType(
  id: string,
  body: Partial<{
    display_name: string;
    description: string;
    sort_order: number;
    is_active: boolean;
    type_key: string;
    metadata: Record<string, unknown>;
  }>,
) {
  return dealTypesFetch<{ ok: true; sale_type: OpsDealSaleType }>(q("sale-types", { id }), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function deleteDealSaleType(id: string) {
  return dealTypesFetch<{ ok: true }>(q("sale-types", { id }), { method: "DELETE" });
}

export async function listDealProductSaleLinks(productTypeId?: string) {
  return dealTypesFetch<{ ok: true; links: OpsDealProductSaleLink[] }>(
    q("links", productTypeId ? { product_type_id: productTypeId } : undefined),
  );
}

export async function setDealProductSaleLinks(productTypeId: string, saleTypeIds: string[]) {
  return dealTypesFetch<{ ok: true; links: OpsDealProductSaleLink[] }>(q("links"), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ product_type_id: productTypeId, sale_type_ids: saleTypeIds }),
  });
}

export async function listDealTypeSlots(opts: { productTypeId?: string; saleTypeId?: string }) {
  return dealTypesFetch<{ ok: true; slots: OpsDealTypeSlot[] }>(
    q("slots", {
      product_type_id: opts.productTypeId ?? "",
      sale_type_id: opts.saleTypeId ?? "",
    }),
  );
}

export async function createDealTypeSlot(body: {
  product_type_id?: string | null;
  sale_type_id?: string | null;
  slot_key?: string;
  label: string;
  helper_text?: string | null;
  slot_kind?: OpsDealTypeSlotKind;
  packet_layer?: "ops" | "plant";
  required?: boolean;
  sort_order?: number;
}) {
  return dealTypesFetch<{ ok: true; slot: OpsDealTypeSlot }>(q("slots"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function patchDealTypeSlot(
  id: string,
  body: Partial<{
    label: string;
    helper_text: string | null;
    slot_kind: OpsDealTypeSlotKind;
    packet_layer?: "ops" | "plant";
    required: boolean;
    sort_order: number;
    sale_type_id: string | null;
    slot_key: string;
  }>,
) {
  return dealTypesFetch<{ ok: true; slot: OpsDealTypeSlot }>(q("slots", { id }), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function deleteDealTypeSlot(id: string) {
  return dealTypesFetch<{ ok: true }>(q("slots", { id }), { method: "DELETE" });
}
